import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { Link } from 'react-router-dom';
import DataTable, { createTheme } from 'react-data-table-component';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import api from '../../services/api';

import ViewProjectModal from './ViewProjectModal';
import EditProjectModal from './EditProjectModal';
import DeleteConfirmationModal from '../DeleteConfirmationModal';

// 1. Create a custom theme (optional)
createTheme('customTheme', {
  // ... (your theme settings - kept for brevity)
  text: {
    primary: '#212529',
    secondary: '#495057',
  },
  background: {
    default: '#ffffff',
  },
  context: {
    background: '#e3f2fd',
    text: '#212529',
  },
  divider: {
    default: '#dee2e6',
  },
  action: {
    button: '#fff',
    hover: 'rgba(0,0,0,0.08)',
    disabled: 'rgba(0,0,0,0.12)',
  },
});

// 2. Custom styles
const customStyles = {
  // ... (your custom styles - kept for brevity)
  rows: {
    style: {
      minHeight: '50px', // row height
      fontSize: '15px',
    },
  },
  headCells: {
    style: {
      backgroundColor: '#f8f9fa',
      fontWeight: '600',
      fontSize: '1rem',
    },
  },
  cells: {
    style: {
      fontSize: '0.95rem',
    },
  },
};


const ProjectsTable = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userType, setUserType] = useState(null);

  // Pagination state
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  // Sorting state
  const [sortColumn, setSortColumn] = useState(''); // empty = default (sponsored first, random)
  const [sortDirection, setSortDirection] = useState('asc');

  // For modals
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);

  const [currentProject, setCurrentProject] = useState(null);
  const [editedProject, setEditedProject] = useState(null);
  const [deletedProject, setDeletedProject] = useState(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [launchFilter, setLaunchFilter] = useState('');
  const [sponsoredFilter, setSponsoredFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Cached commission companies (fetched once on mount)
  const [commissionCompanies, setCommissionCompanies] = useState([]);

  // Fetch user and commission companies on mount
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      setUserType(parsedUser.type);
    }

    // Fetch commission companies once and cache them
    const fetchCommissionCompanies = async () => {
      try {
        const response = await api.get('/commission-companies/unpaginated');
        if (response.data.status) {
          const companies = response.data.response?.data || response.data.response || [];
          setCommissionCompanies(Array.isArray(companies) ? companies : []);
        }
      } catch (error) {
        console.error('Error fetching commission companies:', error);
      }
    };
    fetchCommissionCompanies();
  }, []);

  // Fetch projects when pagination, sorting, filters, or userType changes
  useEffect(() => {
    if (userType) {
      fetchProjects(currentPage, perPage, sortColumn, sortDirection, searchTerm, statusFilter, launchFilter, sponsoredFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userType, currentPage, perPage, sortColumn, sortDirection]);

  // Trigger search/filter when filters change (with debounce for search)
  useEffect(() => {
    if (!userType) return;
    const debounceTimer = setTimeout(() => {
      setCurrentPage(1); // Reset to page 1 on filter change
      fetchProjects(1, perPage, sortColumn, sortDirection, searchTerm, statusFilter, launchFilter, sponsoredFilter);
    }, 400);
    return () => clearTimeout(debounceTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, statusFilter, launchFilter, sponsoredFilter]);

  // Refresh projects after modals close
  const refreshProjects = () => {
    if (userType) {
      fetchProjects(currentPage, perPage, sortColumn, sortDirection, searchTerm, statusFilter, launchFilter, sponsoredFilter);
    }
  };

  const fetchProjects = async (page, limit, sort, direction, search, status, launch, sponsored) => {
    setLoading(true);
    try {
      // Build query params
      let endpoint;
      if (userType === 'admin') {
        let queryParams = `page=${page}&per_page=${limit}`;
        
        // Add sort parameter if a column is selected
        if (sort) {
          // Format: sort=-column for desc, sort=column for asc
          const sortParam = direction === 'desc' ? `-${sort}` : sort;
          queryParams += `&sort=${sortParam}`;
        }

        // Use search param - searches project name and developer name automatically
        if (search) {
          queryParams += `&search=${encodeURIComponent(search)}`;
        }

        // Add server-side filters
        if (status) {
          queryParams += `&status=${encodeURIComponent(status)}`;
        }
        if (launch) {
          queryParams += `&isLaunch=${launch === 'yes' ? 1 : 0}`;
        }
        if (sponsored) {
          queryParams += `&isSponsored=${sponsored === 'yes' ? 1 : 0}`;
        }
        
        endpoint = `/projects?${queryParams}`;
      } else {
        endpoint = '/projects/developer/projects';
      }
      
      const { data } = await api.get(endpoint);

      if (data.status) {
        // Handle paginated response
        const projectData = Array.isArray(data.response?.data) ? data.response.data : [];
        setProjects(projectData);
        
        // Set total rows for pagination (admin uses paginated response, others might not)
        if (data.response?.total !== undefined) {
          setTotalRows(data.response.total);
        } else {
          setTotalRows(projectData.length);
        }
      } else {
        setProjects([]);
        setTotalRows(0);
        console.error('Error fetching projects:', data.message);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      setProjects([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  };

  // Handle sort from DataTable
  const handleSort = (column, direction) => {
    // Use the sortField defined in the column, fallback to 'id'
    const fieldName = column.sortField || 'id';
    setSortColumn(fieldName);
    setSortDirection(direction);
    setCurrentPage(1); // Reset to page 1 when sorting changes
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Handle rows per page change
  const handlePerRowsChange = (newPerPage, page) => {
    setPerPage(newPerPage);
    setCurrentPage(page);
  };


  // ----- View -----
  const handleView = (project) => {
    setCurrentProject(project);
    setShowViewModal(true);
  };
  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setCurrentProject(null);
    refreshProjects();
  };

  // ----- Edit -----
  const handleEdit = (project) => {
    setEditedProject(project);
    setShowEditModal(true);
  };
  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditedProject(null);
    refreshProjects();
  };

  // ----- Delete -----
  const handleDeleteProject = (project) => {
    setDeletedProject(project);
    setShowDeleteConfirmationModal(true);
  };
  const handleCloseDeleteModal = () => {
    setShowDeleteConfirmationModal(false);
    setDeletedProject(null);
    refreshProjects();
  };

  const handleDelete = async () => {
    if (!deletedProject) return; // Guard clause
    try {
      await api.delete(`/projects/${deletedProject.id}`);
      if (userType === 'admin') {
        toast.success('Project Deleted Successfully!');
      } else {
        toast.success('Deletion request is submitted! Wait for approval.');
      }
    } catch (error) {
      console.error('Error deleting project:', error);
      const errorMessage = error.response?.data?.message || error.message || 'An unknown error occurred';
      toast.error(`Error deleting project: ${errorMessage}`);
    } finally {
      handleCloseDeleteModal(); // Refresh happens inside this now
    }
  };


  // ----- Filters -----
  const handleStatusFilterChange = (e) => setStatusFilter(e.target.value);
  const handleLaunchFilterChange = (e) => setLaunchFilter(e.target.value);
  const handleSponsoredFilterChange = (e) => setSponsoredFilter(e.target.value);
  const handleSearchChange = (e) => setSearchTerm(e.target.value);


  // ----- Table Columns -----
  const columns = [
    {
      name: 'Project ID',
      selector: (row) => row.id,
      sortable: true,
      sortField: 'id',
      wrap: true,
    },
    {
      name: 'Developer',
      selector: (row) => row.developer?.name || 'N/A',
      sortable: true,
      sortField: 'developer_id',
      wrap: true,
    },
    {
      name: 'Name',
      selector: (row) => row.name,
      sortable: true,
      sortField: 'name',
      wrap: true,
    },
    {
      name: 'Launch',
      selector: (row) => row.isLaunch,
      sortable: true,
      sortField: 'isLaunch',
      cell: (row) => (
        <span
          className={`badge rounded-pill px-3 py-2 bg-${row.isLaunch ? 'success' : 'warning'
            } text-white`}
        >
          {row.isLaunch ? 'Yes' : 'No'}
        </span>
      ),
    },
    {
      name: 'Sponsored',
      selector: (row) => row.isSponsored,
      sortable: true,
      sortField: 'isSponsored',
      cell: (row) => (
        <span
          className={`badge rounded-pill px-3 py-2 bg-${row.isSponsored ? 'success' : 'warning'
            } text-white`}
        >
          {row.isSponsored ? 'Yes' : 'No'}
        </span>
      ),
    },
    {
      name: 'Status',
      selector: (row) => row.status,
      sortField: 'status',
      sortable: true,
      cell: (row) => {
        let colorClass = 'warning';
        let statusLabel = 'Pending'; // Default

        // Handle potential null/undefined status
        const currentStatus = row.status ? row.status.toLowerCase() : 'pending';

        if (currentStatus === 'approved') {
          colorClass = 'success';
          statusLabel = 'Approved';
        } else if (currentStatus === 'rejected' || currentStatus === 'declined') { // Accept both 'rejected' and 'declined'
          colorClass = 'danger';
          statusLabel = 'Rejected'; // Or 'Declined' if you prefer
        } else {
          // Explicitly handle 'pending' or any other status
          colorClass = 'warning';
          statusLabel = 'Pending';
        }

        return (
          <span className={`badge rounded-pill px-3 py-2 bg-${colorClass} text-white`}>
            {statusLabel}
          </span>
        );
      },
    },
    {
      name: 'Actions',
      cell: (row) => (
        <div>
          {/* View Button */}
          <button
            onClick={() => handleView(row)}
            className="w-32-px h-32-px me-1 me-md-2 bg-primary-light text-primary-600 rounded-circle d-inline-flex align-items-center justify-content-center border-0" // Added border-0 for consistency
            title="View Details" // Added title for accessibility
          >
            <Icon icon="iconamoon:eye-light" />
          </button>
          {/* Edit Button */}
          <button
            onClick={() => handleEdit(row)}
            className="w-32-px h-32-px me-1 me-md-2 bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center border-0"
            title="Edit Project"
          >
            <Icon icon="lucide:edit" />
          </button>
          {/* Delete Button */}
          <button
            onClick={() => handleDeleteProject(row)}
            className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center border-0"
            title="Delete Project"
          >
            <Icon icon="mingcute:delete-2-line" />
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: '130px', // Optional: Adjust width if buttons wrap undesirably
    },
  ];


  return (
    <div className="card basic-data-table p-3">
      {/* Filters Row */}
      <div className="row mb-3 gy-3 align-items-end"> {/* Added gy-3 for vertical spacing on smaller screens */}
        {/* Search Bar Input - Step 2 */}
        <div className="col-md-3">
          <label htmlFor="projectSearch" className="form-label fw-bold">Search</label>
          <input
            id="projectSearch" // Added id for label association
            type="text"
            className="form-control"
            placeholder="Search by ID, Name, Status..."
            value={searchTerm}
            onChange={handleSearchChange} // Use the handler
          />
        </div>

        {/* Status Filter */}
        <div className="col-md-2"> {/* Adjusted column size */}
          <label htmlFor="statusFilter" className="form-label fw-bold">Status</label>
          <select
            id="statusFilter"
            className="form-select"
            value={statusFilter}
            onChange={handleStatusFilterChange}
          >
            <option value="">All</option>
            <option value="approved">Approved</option>
            <option value="pending">Pending</option>
            <option value="rejected">Rejected</option> {/* Changed from declined to rejected based on your code */}
          </select>
        </div>
        {/* Launch Filter */}
        <div className="col-md-2"> {/* Adjusted column size */}
          <label htmlFor="launchFilter" className="form-label fw-bold">Launch</label>
          <select
            id="launchFilter"
            className="form-select"
            value={launchFilter}
            onChange={handleLaunchFilterChange}
          >
            <option value="">All</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </div>
        {/* Sponsored Filter */}
        <div className="col-md-2"> {/* Adjusted column size */}
          <label htmlFor="sponsoredFilter" className="form-label fw-bold">Sponsored</label>
          <select
            id="sponsoredFilter"
            className="form-select"
            value={sponsoredFilter}
            onChange={handleSponsoredFilterChange}
          >
            <option value="">All</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </div>
        {/* Add New Button */}
        <div className="col-md-3 text-md-end"> {/* Adjusted column size and alignment */}
          <Link
            to="/add-project"
            className="btn btn-primary btn-sm d-inline-flex align-items-center gap-2 w-100 w-md-auto" // Adjusted width for responsiveness
          >
            <Icon icon="ic:baseline-plus" className="icon fs-6" /> {/* Used fs-6 instead of text-sm */}
            Add New Project
          </Link>
        </div>
      </div>

      {/* DataTable */}
      <div className="table-responsive">
        <DataTable
          columns={columns}
          data={projects}
          progressPending={loading}
          theme="customTheme"
          customStyles={customStyles}
          pagination
          paginationServer={userType === 'admin'}
          paginationTotalRows={totalRows}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
          paginationPerPage={perPage}
          paginationRowsPerPageOptions={[10, 25, 50, 100]}
          sortServer={userType === 'admin'}
          onSort={handleSort}
          responsive
          highlightOnHover
          striped
          noDataComponent={loading ? "" : <div className="p-4 text-center">No results found matching your criteria.</div>}
          paginationComponentOptions={{
            rowsPerPageText: 'Rows per page:',
            rangeSeparatorText: 'of',
          }}
        />
      </div>


      {/* View Modal */}
      {showViewModal && currentProject && (
        <ViewProjectModal
          show={showViewModal}
          handleClose={handleCloseViewModal}
          project={currentProject}
          commissionCompanies={commissionCompanies}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && editedProject && (
        <EditProjectModal
          show={showEditModal}
          handleClose={handleCloseEditModal}
          project={editedProject}
          commissionCompanies={commissionCompanies}
        />
      )}

      {/* Delete Confirmation Modal */}
      {/* Keep existing DeleteConfirmationModal logic, but ensure it handles the state correctly */}
      <DeleteConfirmationModal
        show={showDeleteConfirmationModal}
        handleClose={handleCloseDeleteModal}
        handleDelete={handleDelete}
        // Pass necessary props or use children as before
        itemType="Project" // Example of passing item type for generic modal
        itemName={deletedProject ? deletedProject.name : ''} // Example: display name
      >
        {/* You can customize the content passed to DeleteConfirmationModal */}
        {/* Or define the content structure inside the DeleteConfirmationModal itself */}
        <Modal.Header closeButton>
          <Modal.Title>Delete Project</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the project "{deletedProject?.name || 'this project'}"?
          {userType !== 'admin' && <p className="text-warning mt-2 small">Note: As a developer, this will submit a deletion request for admin approval.</p>}
        </Modal.Body>
      </DeleteConfirmationModal>
    </div>
  );
};

export default ProjectsTable;